import moment from 'moment';
import React, { memo, useEffect, useState } from 'react';
import { ModalType } from '../../../Constants/Procurement/LiveAuction/constants';

interface RemaininTimeProps {
  type?: 'full' | 'hr mm';
  endTime: number;
  startTime: number;
  isPause?: boolean;
  isLive?: boolean;
  open: ModalType | null;
}

interface ValueType {
  hours: string;
  minutes: string;
  seconds: string;
  milliseconds: string;
}
const initialValue: ValueType = {
  hours: '00',
  minutes: '00',
  seconds: '00',
  milliseconds: '000',
};
let intervalId: any;
const RemainingTime: React.FC<RemaininTimeProps> = ({
  type = 'full',
  endTime,
  startTime,
  isPause,
  isLive,
  open,
}) => {
  const [value, setValue] = useState<ValueType>(initialValue);

  useEffect(() => {
    if (checkDiff() && !isPause && isLive) {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        checkDiff();
      }, 1);
    } else if (!isPause) setValue(initialValue);
    return () => clearInterval(intervalId);
  }, [endTime, isPause, isLive, open]);

  const formatTimeUnit = (value: number): string => {
    return value < 10 ? `0${value}` : value.toString();
  };

  const formatMilliseconds = (value: number): string => {
    return value < 10
      ? `00${value}`
      : value < 100
      ? `0${value}`
      : value.toString();
  };
  const checkDiff = () => {
    const now = moment();
    const end = moment.unix(endTime);
    const start = moment.unix(startTime);
    const duration = moment.duration(end.diff(now));
    const hours = formatTimeUnit(duration.hours());
    const minutes = formatTimeUnit(duration.minutes());
    const seconds = formatTimeUnit(duration.seconds());
    const milliseconds = formatMilliseconds(duration.milliseconds());
    if (end > now) {
      setValue({
        hours,
        minutes,
        seconds,
        milliseconds,
      });
    }
    return start.diff(now) < 0;
  };

  return (
    <div className="w-max bg-variant-cyan3 text-blue h-[69px] px-2 rounded-md flex items-center gap-2 divide-x-2 divide-blue divide-opacity-10 text-[22px] font-semibold">
      <div>
        <svg
          width="53"
          height="53"
          viewBox="0 0 53 53"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M26.474 3.28749C26.3938 3.28923 26.3138 3.2968 26.2347 3.31014H21.5312C21.3118 3.30704 21.0939 3.34758 20.8902 3.42942C20.6865 3.51127 20.5012 3.63277 20.3449 3.78687C20.1886 3.94098 20.0645 4.12461 19.9797 4.3271C19.895 4.52959 19.8514 4.74689 19.8514 4.96639C19.8514 5.18588 19.895 5.40319 19.9797 5.60568C20.0645 5.80817 20.1886 5.9918 20.3449 6.1459C20.5012 6.30001 20.6865 6.42151 20.8902 6.50335C21.0939 6.5852 21.3118 6.62574 21.5312 6.62264H24.8438V10.0193C14.6605 10.8661 6.625 19.4124 6.625 29.8098C6.625 40.7669 15.5429 49.6848 26.5 49.6848C37.4571 49.6848 46.375 40.7669 46.375 29.8098C46.375 24.9265 44.5955 20.4543 41.6618 16.9901L44.0654 14.5866L45.2396 15.7576C45.3923 15.9162 45.5751 16.0428 45.7773 16.1299C45.9795 16.217 46.197 16.263 46.4172 16.2651C46.6373 16.2672 46.8557 16.2254 47.0595 16.1421C47.2633 16.0588 47.4485 15.9357 47.6042 15.78C47.7598 15.6243 47.8829 15.4392 47.9662 15.2354C48.0495 15.0316 48.0913 14.8132 48.0892 14.593C48.0872 14.3729 48.0412 14.1553 47.954 13.9531C47.8669 13.751 47.7403 13.5682 47.5817 13.4155L45.4531 11.2902C45.3417 11.1289 45.2026 10.9887 45.0423 10.8761L42.8943 8.72811C42.7399 8.56946 42.5553 8.44334 42.3514 8.35722C42.1475 8.2711 41.9284 8.22673 41.7071 8.22672C41.3776 8.2268 41.0556 8.32515 40.7823 8.50918C40.509 8.69321 40.2968 8.95457 40.1728 9.25983C40.0488 9.5651 40.0187 9.90041 40.0863 10.2229C40.1539 10.5454 40.3161 10.8404 40.5522 11.0702L41.7232 12.2444L39.3197 14.648C36.2521 12.0501 32.3911 10.371 28.1562 10.0189V6.62227H31.4687C31.6882 6.62538 31.9061 6.58483 32.1098 6.50299C32.3134 6.42115 32.4988 6.29964 32.6551 6.14554C32.8114 5.99144 32.9355 5.8078 33.0202 5.60531C33.1049 5.40283 33.1486 5.18552 33.1486 4.96602C33.1486 4.74653 33.1049 4.52922 33.0202 4.32674C32.9355 4.12425 32.8114 3.94061 32.6551 3.78651C32.4988 3.6324 32.3134 3.5109 32.1098 3.42906C31.9061 3.34722 31.6882 3.30667 31.4687 3.30977H26.7555C26.6625 3.29423 26.5683 3.28702 26.474 3.28749ZM26.3058 13.2573C26.4391 13.2735 26.5737 13.2735 26.707 13.2573C31.0892 13.3113 35.0492 15.0553 37.9773 17.8703C38.0989 18.051 38.255 18.206 38.4366 18.3264C41.2991 21.3015 43.0625 25.3423 43.0625 29.8102C43.0625 38.977 35.6668 46.3727 26.5 46.3727C17.3332 46.3727 9.9375 38.977 9.9375 29.8102C9.9375 20.7089 17.2301 13.362 26.3058 13.2573Z"
            fill="#1D3679"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M26.4741 19.8498C26.0354 19.8567 25.6174 20.0373 25.3117 20.3521C25.006 20.6668 24.8377 21.09 24.8438 21.5288V26.9374C24.3411 27.2286 23.9235 27.6473 23.6328 28.1514C23.3421 28.6556 23.1886 29.2275 23.1875 29.81C23.1873 30.2466 23.2729 30.679 23.4393 31.0825C23.6057 31.4859 23.8496 31.8525 24.1572 32.1613C24.4649 32.4702 24.8301 32.7151 25.2321 32.8823C25.634 33.0494 26.0649 33.1354 26.5 33.1354C26.9351 33.1354 27.366 33.0494 27.7679 32.8823C28.1699 32.7151 28.5351 32.4702 28.8428 32.1613C29.1504 31.8525 29.3943 31.4859 29.5607 31.0825C29.7271 30.679 29.8127 30.2466 29.8125 29.81C29.812 29.227 29.6587 28.6544 29.368 28.1497C29.0773 27.6449 28.6594 27.2257 28.1563 26.9343V21.5288C28.1593 21.3072 28.1178 21.0872 28.0343 20.8819C27.9508 20.6767 27.827 20.4902 27.6702 20.3337C27.5133 20.1771 27.3266 20.0536 27.1212 19.9705C26.9158 19.8874 26.6957 19.8464 26.4741 19.8498Z"
            fill="#1D3679"
          />
        </svg>
      </div>
      <div className="h-12 pl-2 flex items-center">
        {value?.hours || '00'} Hr
      </div>
      <div className="h-12 pl-2 flex items-center">
        {value?.minutes || '00'} Min
      </div>
      {type === 'full' ? (
        <div className="h-12 pl-2 flex items-center">
          {value?.seconds || '00'} Sec
        </div>
      ) : (
        ''
      )}
      {type === 'full' ? (
        <div className="h-12 pl-2 flex items-center">
          {value?.milliseconds || '000'} M.Sec
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default memo(RemainingTime);
